import React, { useState } from "react";
import { Alert, Button, Modal, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useMutation } from "@apollo/client";
import {
  SUBMIT_DSP_ADJUST_BILL,
  SubmitDspAdjustBillData,
  SubmitDspAdjustBillVariables,
} from "../graphql/mutations/submitDspAdjustBill";
import { downloadDspAdjustBillTemplate } from "../../../../utils/detailAdjustmentCsv";

interface Props {
  billId: string;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_ROWS = 100_000;

export const DspAdjustBillUploadModal: React.FC<Props> = ({
  billId,
  visible,
  onClose,
  onSuccess,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [submitMutation] = useMutation<
    SubmitDspAdjustBillData,
    SubmitDspAdjustBillVariables
  >(SUBMIT_DSP_ADJUST_BILL);

  const handleClose = () => {
    setFileList([]);
    setErrorMsg(null);
    onClose();
  };

  const handleSubmit = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.error("请先选择 CSV 文件");
      return;
    }

    // 行数校验
    const text = await file.text();
    const lines = text.split("\n").filter((l) => l.trim().length > 0);
    const dataRows = lines.length - 1; // 去除表头
    if (dataRows > MAX_ROWS) {
      message.error(
        `CSV 数据行数 ${dataRows} 超过上限 ${MAX_ROWS.toLocaleString()}，请拆分后上传`
      );
      return;
    }

    // 读取 Base64
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      setSubmitting(true);
      try {
        await submitMutation({
          variables: { billId, fileBase64: base64, fileName: file.name },
        });
        message.success("调账任务已提交，请稍后查看任务状态");
        setFileList([]);
        onSuccess();
        handleClose();
      } catch (e: any) {
        setErrorMsg(e?.message || "提交失败，请重试");
      } finally {
        setSubmitting(false);
      }
    };
    reader.onerror = () => {
      message.error("文件读取失败，请重试");
    };
  };

  return (
    <Modal
      title="DSP 账单明细调账"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          提交
        </Button>,
      ]}
    >
      <Button
        type="link"
        onClick={downloadDspAdjustBillTemplate}
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        下载调账模板
      </Button>
      {errorMsg && (
        <Alert
          message={errorMsg}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Upload.Dragger
        accept=".csv"
        maxCount={1}
        fileList={fileList}
        beforeUpload={(file) => {
          if (!file.name.toLowerCase().endsWith(".csv")) {
            message.error("仅支持 .csv 格式文件");
            return Upload.LIST_IGNORE;
          }
          if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            message.error(`文件大小不能超过 ${MAX_FILE_SIZE_MB}MB`);
            return Upload.LIST_IGNORE;
          }
          return false; // 阻止自动上传
        }}
        onChange={({ fileList: fl }) => setFileList(fl)}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽上传 CSV 文件</p>
        <p className="ant-upload-hint">
          仅支持 .csv 格式，文件大小 ≤ {MAX_FILE_SIZE_MB}MB，数据行数 ≤{" "}
          {MAX_ROWS.toLocaleString()} 行
        </p>
      </Upload.Dragger>
    </Modal>
  );
};
